﻿import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { SatserUtlandModel } from '../Models/SatserUtlandModel';
import * as ValgTypes from '../Store/Valg/Types';
import { ResultatValgVisning } from '../Store/Resultat/Types';
import VeilederUtvekslingDto from '../Models/VeilederUtvekslingDto';
import { EpiContext } from '../../Shared/Scripts/Store/EpiContext/Types';
import contentResolver from '../../Shared/Scripts/Utilities/EpiContentResolver';
import { resolveRawText } from '../../Shared/Scripts/Utilities/EpiContentResolver';
import { VeilederUtvekslingAppState } from '../Store';
import VerdensdelValg from '../Utilities/VerdensdelValg';
import * as UtregningUtland from '../Utilities/Utregning';
import VarighetValg from '../Utilities/VarighetValg';
import UtbetalingsPlanDetaljer from './UtbetalingsPlanDetaljer';
import { updateLiveMessage } from '../../Shared/Scripts/Utilities/LiveMessage';

interface UtbetalingOwnProps {
    satser: SatserUtlandModel | null;
    valg: ValgTypes.ValgState;
    resultat: ResultatValgVisning;
    locale: string;
    valuta: string;
}

type UtbetalingProps = UtbetalingOwnProps & MapStateToProps;

function Utbetalingsplan(props: UtbetalingProps): JSX.Element {
    const { epiContext } = props;
    useEffect(() => {
        updateLiveMessage();
    }, []);

    let brokNevner: number = 1;
    if (props.valg.aVarighet?.value === VarighetValg.valgHel) brokNevner = 2;

    let utbetalingsDetaljer: JSX.Element | null = null;
    if (props.resultat.visBoksFa) {
        utbetalingsDetaljer = (
            <UtbetalingsPlanDetaljer
                valg={props.valg}
                satser={props.satser}
                locale={props.locale}
                valuta={props.valuta}
            />
        );
    }

    function hentUtbetalingsMnd(): string {
        if (
            props.valg.bVerdensdel?.value === VerdensdelValg.b5_oseania ||
            props.valg.bVerdensdel?.value === VerdensdelValg.b6_sorAmerika
        ) {
            return resolveRawText(epiContext, 'mndJuni');
        } else {
            return resolveRawText(epiContext, 'mndJuli');
        }
    }

    function hentBasisStotteHalvtÅr(): number {
        if (props.satser === null || props.satser === undefined) return 0;

        if (
            props.satser.SATS_ST01_MAKS_BASISSTOTTE_DAG === null ||
            props.satser.SATS_ST01_MAKS_BASISSTOTTE_DAG === undefined
        )
            return 0;

        return Math.round(props.satser.SATS_ST01_MAKS_BASISSTOTTE_DAG * 150);
    }

    function hentBasisStotteEnMnd(): number {
        if (props.satser === null || props.satser === undefined) return 0;

        if (
            props.satser.SATS_ST01_MAKS_BASISSTOTTE_DAG === null ||
            props.satser.SATS_ST01_MAKS_BASISSTOTTE_DAG === undefined
        )
            return 0;

        return Math.round(props.satser.SATS_ST01_MAKS_BASISSTOTTE_DAG * 30);
    }

    function hentHtmlForHostSemester(): JSX.Element | null {
        let html: JSX.Element | null = null;

        if (
            props.valg.aVarighet?.value === VarighetValg.valgHel ||
            props.valg.aVarighet?.value === VarighetValg.valgHost
        ) {
            html = (
                <div className="c-veileder__content">
                    <h4 className="c-veileder__tittel c-veileder__tittel--info">
                        {contentResolver(epiContext, 'kolonneTittelHost')}
                    </h4>
                    <div role="table" className="c-table">
                        <div role="row" className="c-table__header-row">
                            <div role="columnheader" className="c-table__header-cell">
                                {contentResolver(epiContext, 'kolonneTittelDatoUtbetalingsplan')}
                            </div>
                            <div role="columnheader" className="c-table__header-cell">
                                {contentResolver(epiContext, 'kolonneTittelBelopUtbetalingsplan')}
                            </div>
                        </div>
                        <div role="row" className="c-table__data-row">
                            <div role="cell" className="c-table__data-cell">
                                {hentUtbetalingsMnd()}
                            </div>
                            <div role="cell" className="c-table__data-cell">
                                {
                                    //tar minus 0.1 for å unngå feil i avrundingen.
                                    //dersom svaret blir 0.5 så avrunder desember alltid ned, og juni/juli alltid opp.
                                    UtregningUtland.formatNumber(
                                        Math.round(
                                            (UtregningUtland.regnUtSkolepengerStotte(props.valg, props.satser)
                                                .totaltStipendOgLan +
                                                UtregningUtland.regnUtReiseStotte(props.valg, props.satser)
                                                    .totaltStipendOgLan) /
                                                brokNevner -
                                                0.1 +
                                                UtregningUtland.regnUtRekrutteringsstipendUtveksling(
                                                    props.valg,
                                                    props.satser,
                                                ) /
                                                    brokNevner,
                                        ) + hentBasisStotteHalvtÅr(),
                                        props.locale,
                                    )
                                }
                                &nbsp;{props.valuta}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return html;
    }

    function hentHtmlForVaarSemester(): JSX.Element | null {
        let html: JSX.Element | null = null;

        if (
            props.valg.aVarighet?.value === VarighetValg.valgHel ||
            props.valg.aVarighet?.value === VarighetValg.valgVaar
        ) {
            html = (
                <div className="c-veileder__content">
                    <h4 className="c-veileder__tittel c-veileder__tittel--info">
                        {contentResolver(epiContext, 'kolonneTittelVar')}
                    </h4>
                    <div role="table" className="c-table">
                        <div role="row" className="c-table__header-row">
                            <div role="columnheader" className="c-table__header-cell c-table__header-cell--lilac">
                                {contentResolver(epiContext, 'kolonneTittelDatoUtbetalingsplan')}
                            </div>
                            <div role="columnheader" className="c-table__header-cell c-table__header-cell--lilac">
                                {contentResolver(epiContext, 'kolonneTittelBelopUtbetalingsplan')}
                            </div>
                        </div>
                        <div role="row" className="c-table__data-row">
                            <div role="cell" className="c-table__data-cell">
                                {contentResolver(epiContext, 'mndDesember')}
                            </div>
                            <div role="cell" className="c-table__data-cell">
                                {UtregningUtland.formatNumber(
                                    Math.round(
                                        (UtregningUtland.regnUtSkolepengerStotte(props.valg, props.satser)
                                            .totaltStipendOgLan +
                                            UtregningUtland.regnUtReiseStotte(props.valg, props.satser)
                                                .totaltStipendOgLan) /
                                            brokNevner,
                                    ) +
                                        hentBasisStotteHalvtÅr() +
                                        UtregningUtland.regnUtRekrutteringsstipendUtveksling(props.valg, props.satser) /
                                            brokNevner,
                                    props.locale,
                                )}
                                &nbsp;{props.valuta}
                            </div>
                        </div>
                        <div role="row" className="c-table__data-row">
                            <div role="cell" className="c-table__data-cell">
                                {contentResolver(epiContext, 'mndJuni')}
                            </div>
                            <div role="cell" className="c-table__data-cell">
                                {UtregningUtland.formatNumber(hentBasisStotteEnMnd(), props.locale)}
                                &nbsp;{props.valuta}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return html;
    }

    return (
        <div className="c-veileder__panel c-veileder__panel--info c-veileder__panel--white-aubergine">
            <div className="c-veileder__panel-row">
                {hentHtmlForHostSemester()}
                {hentHtmlForVaarSemester()}
            </div>
            <div className="c-veileder__panel-row">
                <div className="c-veileder__content">
                    <h4 className="c-veileder__tittel c-veileder__tittel--info">
                        {contentResolver(epiContext, 'tittelVenstreSpalte')}
                    </h4>
                    {contentResolver(epiContext, 'venstreSpalte')}
                </div>
                <div className="c-veileder__content">
                    <h4 className="c-veileder__tittel c-veileder__tittel--info">
                        {contentResolver(epiContext, 'tittelHoyreSpalte')}
                    </h4>
                    {contentResolver(epiContext, 'hoyreSpalte')}
                </div>
            </div>
            <div className="c-veileder__panel-row">
                <div className="c-veileder__content--horizontal-center">
                    <div className="c-veileder__content c-veileder__content-bredere">
                        <div className="c-infoboks--egg-sunshine">{utbetalingsDetaljer}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface MapStateToProps {
    epiContext: EpiContext<VeilederUtvekslingDto>;
}

export default connect(
    (state: VeilederUtvekslingAppState): MapStateToProps => {
        return {
            epiContext: state.epiContext,
        };
    },
)(Utbetalingsplan);
